"use client";

import { Controller, type Control, type FieldPath, type FieldValues } from "react-hook-form";

import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useFieldMessage } from "@/i18n/use-field-message";

type TextFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  /** DOM id; defaults to the name. Set it when two forms on a page share a field name. */
  id?: string;
  label: string;
  type?: "text" | "email" | "password";
  autoComplete?: string;
  /** e.g. "numeric" for one-time codes. */
  inputMode?: "text" | "numeric";
  description?: string;
  /** Rendered next to the label, e.g. a "Forgot your password?" link. */
  labelAction?: React.ReactNode;
  disabled?: boolean;
};

/** One labelled input wired to React Hook Form, with its error under it. */
export function TextField<T extends FieldValues>({
  control,
  name,
  id = name,
  label,
  type = "text",
  autoComplete,
  inputMode,
  description,
  labelAction,
  disabled,
}: TextFieldProps<T>) {
  const fieldMessage = useFieldMessage();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <div className="flex items-center">
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            {labelAction && <div className="ml-auto">{labelAction}</div>}
          </div>
          <Input
            {...field}
            id={id}
            type={type}
            autoComplete={autoComplete}
            inputMode={inputMode}
            disabled={disabled}
            aria-invalid={fieldState.invalid}
            aria-describedby={description ? `${id}-description` : undefined}
            className="bg-background"
          />
          {description && !fieldState.invalid && (
            <FieldDescription id={`${id}-description`}>{description}</FieldDescription>
          )}
          {fieldState.invalid && (
            <FieldError errors={[{ message: fieldMessage(fieldState.error?.message) }]} />
          )}
        </Field>
      )}
    />
  );
}
